/*
  Pixelpa — Photoshop-style "Save As" modal (Export).

  Self-contained UI component. Construct once; call `open(initial?)` to show.
  Resolves with the chosen export spec on Save / Enter, or `null` on
  Cancel / Escape / outside-click.

  Wires into `exportDocument()` in `src/app/io/encodeImage.ts` — the caller
  passes the document in and receives the blob + filename back.

  Format support:
    - PNG (lossless, no quality slider)
    - JPEG (quality 0..1, white background forced)
    - WebP (quality 0..1, supports transparency)
    - PSD (Photoshop document, preserves layers)
    - TIFF (lossless, supports transparency — #i217)

  NOTE (#i217): the "tiff" SaveFormat/FORMATS entry below only covers the
  dialog's own data model + UI (format dropdown, filename extension,
  quality-slider visibility, transparency warning suppression). The actual
  encode dispatch lives in main.ts's showSaveAs(), which still only branches
  on "psd"/"bmp"/"gif"/"ico"/"pdf" before falling through to the
  png/jpeg/webp canvas-based exportDocument() path — it needs its own
  `else if (result.format === "tiff")` branch calling encodeTiff() (already
  used by main.ts's separate exportAs() flow) before selecting TIFF here is
  safe to ship; otherwise it silently downloads a PNG mislabeled ".tiff".
*/

import type { Document } from "../../core/document/Document";
import { exportDocument, downloadBlob, type ExportFormat } from "../io/encodeImage";
import { trapFocus } from "./ColorPicker";

// ---------- Public types ----------

export type SaveFormat = "png" | "jpeg" | "webp" | "psd" | "bmp" | "gif" | "ico" | "pdf" | "tiff";

export interface SaveAsResult {
  format: SaveFormat;
  quality: number; // 0..1, only meaningful for jpeg/webp
  filename: string;
}

// ---------- Format descriptions ----------

interface FormatMeta {
  ext: string;
  label: string;
  mime: ExportFormat | "application/x-psd" | "image/bmp" | "image/gif" | "image/x-icon" | "application/pdf" | "image/tiff";
  hasQuality: boolean;
  alpha: boolean;
}

const FORMATS: Record<SaveFormat, FormatMeta> = {
  png:  { ext: "png",  label: "PNG",         mime: "image/png",              hasQuality: false, alpha: true  },
  jpeg: { ext: "jpg",  label: "JPEG",        mime: "image/jpeg",             hasQuality: true,  alpha: false },
  webp: { ext: "webp", label: "WebP",        mime: "image/webp",             hasQuality: true,  alpha: true  },
  psd:  { ext: "psd",  label: "Photoshop",   mime: "application/x-psd",      hasQuality: false, alpha: true  },
  bmp:  { ext: "bmp",  label: "BMP",         mime: "image/bmp",              hasQuality: false, alpha: false },
  gif:  { ext: "gif",  label: "GIF",         mime: "image/gif",              hasQuality: false, alpha: false },
  ico:  { ext: "ico",  label: "Icon",        mime: "image/x-icon",           hasQuality: false, alpha: true   },
  pdf:  { ext: "pdf",  label: "PDF",         mime: "application/pdf",        hasQuality: false, alpha: false },
  tiff: { ext: "tiff", label: "TIFF",        mime: "image/tiff",             hasQuality: false, alpha: true  },
};

// ---------- DOM helper ----------

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}

// ---------- Component ----------

export class SaveAsDialog {
  private built = false;
  private root!: HTMLDivElement;
  private sheet!: HTMLDivElement;

  // Form controls
  private nameInput!: HTMLInputElement;
  private formatSelect!: HTMLSelectElement;
  private qualityRow!: HTMLDivElement;
  private qualitySlider!: HTMLInputElement;
  private qualityValue!: HTMLSpanElement;
  private warningEl!: HTMLDivElement;

  private btnSave!: HTMLButtonElement;
  private btnCancel!: HTMLButtonElement;

  // State
  private format: SaveFormat = "png";
  private quality = 0.92;

  // Document reference passed at open time — used to determine if doc
  // has transparency so we can show a warning for JPEG.
  private docAtOpen: Document | null = null;

  // Modal lifecycle
  private resolveFn: ((value: SaveAsResult | null) => void) | null = null;
  private keyHandler: ((e: KeyboardEvent) => void) | null = null;
  private outsideHandler: ((e: MouseEvent) => void) | null = null;
  private openerEl: Element | null = null;

  constructor() {
    // Lazy build — DOM deferred until first open().
  }

  // ---------- Public API ----------

  open(doc: Document, initial?: Partial<SaveAsResult>): Promise<SaveAsResult | null> {
    if (!this.built) this.build();

    // Cancel any in-flight prompt.
    if (this.resolveFn) {
      const prev = this.resolveFn;
      this.resolveFn = null;
      prev(null);
    }

    // Remember opener for focus restoration (#47).
    this.openerEl = document.activeElement;

    this.docAtOpen = doc;

    // Seed state.
    this.format = initial?.format ?? "png";
    this.quality = initial?.quality ?? 0.92;

    const baseName = doc.name.replace(/\.[^.]+$/, "") || "Untitled";
    this.nameInput.value = initial?.filename ?? baseName;

    this.refreshAll();

    this.root.style.display = "";

    // Focus name field, select-all for quick rename.
    setTimeout(() => {
      this.nameInput.focus();
      this.nameInput.select();
    }, 0);

    return new Promise<SaveAsResult | null>((resolve) => {
      this.resolveFn = resolve;
    });
  }

  close(): void {
    this.finish(null);
  }

  destroy(): void {
    if (this.keyHandler) window.removeEventListener("keydown", this.keyHandler, true);
    this.keyHandler = null;
    if (this.outsideHandler && this.built) {
      this.root.removeEventListener("mousedown", this.outsideHandler);
    }
    this.outsideHandler = null;
    if (this.built && this.root.parentNode) this.root.parentNode.removeChild(this.root);
    this.built = false;
  }

  // ---------- Build (lazy) ----------

  private build(): void {
    this.root = el("div", "save-as");
    this.root.style.display = "none";

    // ARIA: modal dialog accessibility (#47)
    this.root.setAttribute("role", "dialog");
    this.root.setAttribute("aria-modal", "true");
    this.root.setAttribute("aria-labelledby", "save-as-title");

    this.sheet = el("div", "save-as__sheet");
    this.root.appendChild(this.sheet);

    // Title bar
    const title = el("div", "save-as__title");
    title.id = "save-as-title";
    title.textContent = "Save As";
    this.sheet.appendChild(title);

    // Body
    const body = el("div", "save-as__body");

    // Filename
    this.nameInput = this.mkTextRow(body, "Name", "Untitled");

    // Format selector
    const fmtRow = el("div", "save-as__row");
    const fmtLab = el("label");
    fmtLab.textContent = "Format";
    this.formatSelect = el("select", "save-as__format");
    for (const [key, meta] of Object.entries(FORMATS)) {
      const opt = document.createElement("option");
      opt.value = key;
      opt.textContent = meta.label;
      this.formatSelect.appendChild(opt);
    }
    fmtRow.appendChild(fmtLab);
    fmtRow.appendChild(this.formatSelect);
    body.appendChild(fmtRow);

    // Quality slider row (shown/hidden based on format)
    this.qualityRow = el("div", "save-as__row save-as__quality-row");
    const qLab = el("label");
    qLab.textContent = "Quality";
    const qInner = el("div", "save-as__quality-inner");
    this.qualitySlider = el("input");
    this.qualitySlider.type = "range";
    this.qualitySlider.min = "1";
    this.qualitySlider.max = "100";
    this.qualitySlider.value = "92";
    this.qualitySlider.className = "save-as__slider";
    this.qualityValue = el("span", "save-as__quality-value");
    this.qualityValue.textContent = "92%";
    qInner.appendChild(this.qualitySlider);
    qInner.appendChild(this.qualityValue);
    this.qualityRow.appendChild(qLab);
    this.qualityRow.appendChild(qInner);
    body.appendChild(this.qualityRow);

    // Warning area (for JPEG + transparency or oversize)
    this.warningEl = el("div", "save-as__warning");
    this.warningEl.style.display = "none";
    body.appendChild(this.warningEl);

    this.sheet.appendChild(body);

    // Actions
    const actions = el("div", "save-as__actions");
    this.btnCancel = el("button", "save-as__btn");
    this.btnCancel.type = "button";
    this.btnCancel.textContent = "Cancel";
    this.btnSave = el("button", "save-as__btn save-as__btn--primary");
    this.btnSave.type = "button";
    this.btnSave.textContent = "Save";
    actions.appendChild(this.btnCancel);
    actions.appendChild(this.btnSave);
    this.sheet.appendChild(actions);

    document.body.appendChild(this.root);

    this.wireEvents();
    this.built = true;
  }

  private mkTextRow(parent: HTMLElement, label: string, placeholder: string): HTMLInputElement {
    const row = el("div", "save-as__row");
    const lab = el("label");
    lab.textContent = label;
    const input = el("input");
    input.type = "text";
    input.spellcheck = false;
    input.placeholder = placeholder;
    row.appendChild(lab);
    row.appendChild(input);
    parent.appendChild(row);
    return input;
  }

  // ---------- Wire events ----------

  private wireEvents(): void {
    this.outsideHandler = (e: MouseEvent) => {
      if (e.target === this.root) this.finish(null);
    };
    this.root.addEventListener("mousedown", this.outsideHandler);
    this.sheet.addEventListener("mousedown", (e) => e.stopPropagation());

    this.keyHandler = (e: KeyboardEvent) => {
      if (this.root.style.display === "none") return;
      if (e.key === "Escape") {
        e.preventDefault();
        this.finish(null);
      } else if (e.key === "Enter") {
        e.preventDefault();
        this.commit();
      } else if (e.key === "Tab") {
        trapFocus(e, this.sheet);
      }
    };
    window.addEventListener("keydown", this.keyHandler, true);

    this.btnSave.addEventListener("click", () => this.commit());
    this.btnCancel.addEventListener("click", () => this.finish(null));

    // Format change: toggle quality slider, update warnings
    this.formatSelect.addEventListener("change", () => {
      this.format = this.formatSelect.value as SaveFormat;
      this.refreshFormat();
    });

    // Quality slider
    this.qualitySlider.addEventListener("input", () => {
      this.quality = parseInt(this.qualitySlider.value, 10) / 100;
      this.qualityValue.textContent = `${Math.round(this.quality * 100)}%`;
    });
  }

  // ---------- Refresh / commit ----------

  private refreshAll(): void {
    this.formatSelect.value = this.format;
    this.qualitySlider.value = String(Math.round(this.quality * 100));
    this.qualityValue.textContent = `${Math.round(this.quality * 100)}%`;
    this.refreshFormat();
  }

  private refreshFormat(): void {
    const meta = FORMATS[this.format];

    // Quality slider visibility
    this.qualityRow.style.display = meta.hasQuality ? "" : "none";

    // Show a warning when saving a potentially-transparent doc as JPEG
    // (JPEG doesn't support alpha — white background is forced).
    let warnings: string[] = [];

    if (this.format === "jpeg" || this.format === "bmp" || this.format === "gif") {
      const doc = this.docAtOpen;
      if (doc && hasTransparency(doc)) {
        const fmtName = this.format === "jpeg" ? "JPEG" : this.format === "bmp" ? "BMP" : "GIF";
        warnings.push(`${fmtName} does not support transparency. Transparent areas will be filled.`);
      }
    }
    const currentName = this.nameInput.value.trim();
    const baseName = currentName.replace(/\.[^.]+$/, "");
    if (currentName !== baseName || !currentName.includes(".")) {
      this.nameInput.value = `${baseName}.${meta.ext}`;
    }

    if (warnings.length > 0) {
      this.warningEl.style.display = "";
      this.warningEl.innerHTML = warnings.map((w) => `<div class="save-as__warning-line">⚠ ${escapeHtml(w)}</div>`).join("");
    } else {
      this.warningEl.style.display = "none";
    }
  }

  private commit(): void {
    const format = this.formatSelect.value as SaveFormat;
    const meta = FORMATS[format];
    const quality = format === "png" ? 1 : this.quality;

    let filename = this.nameInput.value.trim();
    if (filename.length === 0) filename = "Untitled";
    // Ensure extension matches format.
    if (!filename.toLowerCase().endsWith(`.${meta.ext}`)) {
      filename = filename.replace(/\.[^.]+$/, "");
      filename = `${filename}.${meta.ext}`;
    }

    const result: SaveAsResult = {
      format,
      quality,
      filename,
    };
    this.finish(result);
  }

  private finish(value: SaveAsResult | null): void {
    if (!this.resolveFn) return;
    const fn = this.resolveFn;
    this.resolveFn = null;
    if (this.built) this.root.style.display = "none";
    // Restore focus to opener (#47).
    if (this.openerEl && "focus" in this.openerEl) {
      (this.openerEl as HTMLElement).focus();
    }
    this.openerEl = null;
    fn(value);
  }
}

// ---------- Helpers ----------

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] ?? c));
}

/**
 * Quick check whether a document has any visible transparent pixels.
 * Scans visible raster layers for any alpha < 255. Returns false for
 * empty or all-opaque docs. Used to warn before JPEG export.
 */
function hasTransparency(doc: Document): boolean {
  for (const layer of doc.layers) {
    if (!layer.visible || layer.kind !== "raster") continue;
    const data = layer.pixels.data;
    // Quick check: scan every 4th pixel's alpha. For large docs we
    // sample every 16th row/col to avoid scanning a 50MP image.
    const w = layer.pixels.width;
    const h = layer.pixels.height;
    if (w <= 0 || h <= 0) continue;
    const step = Math.max(1, Math.ceil(Math.sqrt((w * h) / 10000)));
    for (let y = 0; y < h; y += step) {
      const row = y * w * 4;
      for (let x = 0; x < w; x += step) {
        if ((data[row + x * 4 + 3] ?? 255) < 255) return true;
      }
    }
  }
  return false;
}
